import React, {useContext, useEffect, useState, useRef} from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Avatar from '@mui/material/Avatar';
import Check from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Mode from '@mui/icons-material/Mode';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShortcutOutlinedIcon from '@mui/icons-material/ShortcutOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import RootContext from '../../services/context-states/root-context';
import {assessmentApi} from '../../api/openapi-axios-client';
import {callApi} from '../../api/helpers';
import {stringToColor} from '../../helpers/strings';
import { DialogType, NewShareUser, ShareUser, SharedUser, UsersForSharing, UserResultShare, UserShareReportData } from '../../types';

const ShareReportPage: React.FC = () => {
    const {messageContext, tokenContext} = useContext(RootContext);
    const {token} = tokenContext;
    const {setError, setSuccessMessage} = messageContext;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loaded, setLoaded] = useState<boolean>(false);
    const [dialogToOpen, setDialogToOpen] = useState<DialogType>('ADD');
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);

    const [shareData, setShareData] = useState<UsersForSharing[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<UsersForSharing[]>([]);
    const [usersForEdit, setUsersForEdit] = useState<any[]>([]);
    const [selectedRelationship, setSelectedRelationship] = useState<string>('');
    
    const ref = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const relationshipRef = useRef<HTMLInputElement>(null);
    const otherRef = useRef<HTMLInputElement>(null);
    const firstNameRef = useRef<HTMLInputElement>(null);
    const lastNameRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const getData = async (): Promise<void> => {
            const shareDataResponse = await callApi<UsersForSharing[]>(() => assessmentApi.resultSharing.getUsersSharedWith());
            setShareData(shareDataResponse);
            setLoaded(true);
        };

        if(token && !loaded) {
            getData();
        }
    }, [token, loaded]);

    const handleSelectedUserChanged = (user: UsersForSharing, status: boolean): void => {
        if(status) {
            setSelectedUsers([...selectedUsers, user]);
        } 
        else {
            setSelectedUsers(selectedUsers.filter((selected) => selected.user.id !== user.user.id));
        }
    }

    const handleAddDialogOpen = (): void => {
        setSuccessMessage('');
        const sharedToEdit = selectedUsers.length === 1 ? selectedUsers[0].sharedWith?.map(shared => ({
            ...shared,
            editMode: false
        })) : [];

        setUsersForEdit(sharedToEdit);
        setDialogToOpen('ADD');
        setDialogOpen(true);
    }

    const handleViewPreviewDialogOpen = async (user: ShareUser): Promise<void> => {

        if(user.watchedCount && Number(user.watchedCount) === 0) {
            return;
        }

        setDialogToOpen('LOADING');
        setDialogOpen(true);

        try {
            const reportData = await callApi<UserShareReportData>(() => assessmentApi.resultSharing.getPreviewShareReportData(user.id));
            localStorage.setItem('reportData', JSON.stringify(reportData));
            window.open('/results/share', '_blank', 'noreferrer');
        }
        catch(error) {
            setError('Error loading Report');
        }

        setDialogOpen(false);
    }

    const handleDialogClose = (resultMessage: string | null): void => {
        if(dialogToOpen === 'ADD') {
            setLoaded(false);
            setUsersForEdit([]);
            setSelectedUsers([]);
            setShareData([]);

            if(resultMessage) {
                setSuccessMessage(resultMessage);
            }

            setDialogOpen(false);
        }
        else {
            setDialogOpen(false);
        }
    }

    const handleAdd = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        setError('');
        event.preventDefault();
        const data = new FormData(event.currentTarget);

        const email = data.get('email') as string;
        const firstName = data.get('firstName') as string;
        const lastName = data.get('lastName') as string;
        const relationship = data.get('relationship') as string;
        const other = data.get('other') as string;
        
        if(!email || !firstName || !lastName || !relationship) {
            setError('All fields are required');
            return;
        }

        if(relationship === 'other' && !other) {
            setError('All fields are required');
            return;
        }

        const user: NewShareUser = {
            email,
            firstName,
            lastName,
            relationship: relationship === 'other' ? other : relationship
        }

        await handlePeopleWithAccessChanged(user, 'add');

        if(relationship === 'other' && otherRef.current) {
            otherRef.current.value = '';
        }

        if(emailRef.current) emailRef.current.value = '';
        if(firstNameRef.current) firstNameRef.current.value = '';
        if(lastNameRef.current) lastNameRef.current.value = '';
        if(relationshipRef.current) relationshipRef.current.value = '';
    };

    const handleEdit = (user: SharedUser, editMode: boolean): void => {
        setUsersForEdit(usersForEdit.map((selected) => {
            if(selected.id === user.id) {
                return {
                    ...selected,
                    editMode
                }
            }
            return selected;
        }));
    }

    const handlePeopleWithAccessChanged = async (user: SharedUser | NewShareUser, action: 'delete' | 'add' | 'update'): Promise<void> => {
        if(action === 'delete') {
            const convertedUser = user as SharedUser;
            await callApi<UserResultShare>(() => assessmentApi.resultSharing.deleteShare(convertedUser.id));
            setUsersForEdit(usersForEdit.filter((selected) => selected.id !== convertedUser.id));
        }

        if(action === 'add') {
            const addedUsers: SharedUser[] = [];

            for(const selected of selectedUsers) {
                const response = await callApi<UserResultShare>(() => assessmentApi.resultSharing.createShare({
                    ...user,
                    userId: selected.user.id
                }));

                addedUsers.push({...response, editMode: false});
            }

            if(selectedUsers.length > 1) {
                handleDialogClose('Results Shared');
            }
            else {
                setUsersForEdit([...usersForEdit, ...addedUsers]);
            }
        }

        if(action === 'update') {
            const convertedUser = user as SharedUser;
            await callApi<UserResultShare>(() => assessmentApi.resultSharing.updateShare(user as SharedUser));

            setUsersForEdit(usersForEdit.map((selected) => {
                if(selected.id === convertedUser.id) {
                    return user as SharedUser;
                }
                return selected;
            }));
        }
    }

    const buildPeopleWithAccess = (user: SharedUser | null): React.ReactElement | null => {
        if(!user) {
            return null;
        }

        const colorForBorder = user.views > 0 ? '#04854F' : '#E08600';
        const colorForBackground = user.views > 0 ? '#D6F7E9' : '#F7EAD6';

        return (
            user.editMode ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 1 },
                    width: '100%',
                    minHeight: { xs: 'auto', sm: 60 },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    marginBottom: { xs: 2, sm: 2 },
                    p: { xs: 1.5, sm: 0 },
                    border: { xs: 1, sm: 'none' },
                    borderColor: 'divider',
                    borderRadius: { xs: 1, sm: 0 }
                }}>
                    <TextField autoComplete='email' inputRef={ref} name={`edit-email-${user.id}`} required id='email' label='E-mail' size='small' autoFocus sx={{flex: 1, width: '100%'}} defaultValue={user.email} />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                        <IconButton aria-label='save' onClick={async () => {
                            if(ref.current) {
                                user.email = ref.current.value;
                                await handlePeopleWithAccessChanged(user, 'update');
                                handleEdit(user, false);
                            }
                        }}><Check style={{fontSize:18}}/></IconButton>
                        <IconButton aria-label='cancel' onClick={() => {handleEdit(user, false)}}><CloseIcon style={{fontSize:18}}/></IconButton>
                    </Box>
                </Box>
            ) : (
                <Box key={user.id} sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 1 },
                    width: '100%',
                    minHeight: { xs: 'auto', sm: 60 },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    marginBottom: { xs: 2, sm: 2 },
                    p: { xs: 1.5, sm: 0 },
                    border: { xs: 1, sm: 'none' },
                    borderColor: 'divider',
                    borderRadius: { xs: 1, sm: 0 }
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1.5,
                        alignItems: 'center',
                        flex: 1,
                        width: '100%'
                    }}>
                        <Avatar sx={{bgcolor: stringToColor(`${user.firstName} ${user.lastName}`), height: { xs: 36, sm: 40 }, width: { xs: 36, sm: 40 }}}>{user.firstName[0]}</Avatar>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0, flex: 1, minWidth: 0}}>
                            <Typography sx={{fontWeight: 'bold', fontSize: { xs: 14, sm: 16 }}}>{user.firstName} {user.lastName}</Typography>
                            <Typography sx={{color: 'grey.500', fontSize: { xs: 12, sm: 14 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{user.email}</Typography>
                            <Typography sx={{color: 'grey.500', fontSize: { xs: 12, sm: 14 }, textTransform:'capitalize'}}>{user.relationship}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'row', sm: 'row' },
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: { xs: 'space-between', sm: 'flex-end' }
                    }}>
                        <Box sx={{border: 1, borderStyle:'solid', borderColor: colorForBorder, background: colorForBackground, borderRadius: 2, height: { xs: 32, sm: 40 }, width: { xs: 100, sm: 120 }, display:'flex', alignItems:'center', justifyContent:'center'}}>
                            <Typography sx={{color: colorForBorder, fontSize: { xs: 12, sm: 14 }}}>{user.views > 0 ? 'Active' : 'Pending'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton aria-label='edit' size='small' onClick={()=> handleEdit(user, true)}><Mode style={{fontSize: 18}}/></IconButton>
                            <IconButton aria-label='delete' size='small' onClick={async () => await handlePeopleWithAccessChanged(user, 'delete')}><CloseIcon style={{fontSize: 18}}/></IconButton>
                        </Box>
                    </Box>
                </Box>
            )
        )
    }

    const buildLoadingModal = (): React.ReactElement => {
        return (
            <div style={{display: 'flex', flexDirection: 'column', width: '100%', height:'100%', alignItems: 'center', justifyContent: 'center'}}>
                <CircularProgress sx={{width: 75, height: 75}}/>
                <Typography sx={{fontWeight: 900, fontSize:'24px', marginBottom: '20px', flexGrow:1}}>
                    Building Report ...
                </Typography>
            </div>
        );
    }

    const buildInviteControl = (): React.ReactElement => {
        return (
            <Box component='form' onSubmit={handleAdd} sx={{
                padding: { xs: 2, sm: 2.5, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1.5, sm: 2 },
                alignItems: 'stretch',
                height: '100%'
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 2 },
                    width: '100%'
                }}>
                    <TextField inputRef={emailRef} autoComplete='email' name='email' required id='email' label='E-mail' autoFocus sx={{flex: 1}} />
                    <FormControl id={'relationship-form'} sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                        <InputLabel id='relationship-label'>Relationship</InputLabel>
                        <Select inputRef={relationshipRef} fullWidth label='Relationship' id='relationship' name='relationship' labelId='relationship-label' value={selectedRelationship} onChange={(e: SelectChangeEvent) => setSelectedRelationship(e.target.value)}>
                            <MenuItem value='parent'>Parent</MenuItem>
                            <MenuItem value='teacher'>Teacher</MenuItem>
                            <MenuItem value='mentor'>Mentor</MenuItem>
                            <MenuItem value='counselor'>Counselor</MenuItem>
                            <MenuItem value='other'>Other</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                {selectedRelationship === 'other' && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: { xs: 1.5, sm: 2 },
                    width: '100%'
                }}>
                    <TextField inputRef={otherRef} autoComplete='other' name='other' required id='other' label='Other' autoFocus sx={{flex: 1}} />
                </Box>
                )}
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 2 },
                    width: '100%'
                }}>
                    <TextField inputRef={firstNameRef} autoComplete='given-name' name='firstName' required id='firstName' label='First Name' sx={{flex: 1}} />
                    <TextField inputRef={lastNameRef} autoComplete='family-name' name='lastName' required id='lastName' label='Last Name' sx={{flex: 1}} />
                </Box>
                <div style={{display: 'flex', flexDirection: 'row', gap: 10, width: '100%', justifyContent: 'end'}}>
                    <Button variant='contained' color='primary' type='submit'>Add User</Button>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', gap: 10, width: '100%', height:'100%'}}>
                    <Typography sx={{fontWeight:'bold'}}>People with Access</Typography>
                    <Box sx={{maxHeight: 260, flexGrow:1, flexShrink:1, overflow:'auto'}}>
                        {usersForEdit.map((user) => buildPeopleWithAccess(user))}
                    </Box>
                </div>
                <div style={{display: 'flex', flexDirection: 'row', gap: 10, width: '100%', alignSelf:'end', justifyContent: 'end'}}>
                    <Button variant='contained' color='primary' sx={{width:100}} onClick={() => handleDialogClose('Results Shared')}>Done</Button>
                </div>
            </Box>
        )
    }

    const getDialogToOpen = (): React.ReactElement => {
        switch(dialogToOpen) {
            case 'LOADING':
                return buildLoadingModal();
            case 'ADD':
                return buildInviteControl();
            default:
                return <></>;
        }
    }

    const buildSharedWithOrTooltip = (row: UsersForSharing): React.ReactElement => {

        if(row.user.watchedCount && row.user.watchedCount > 0) {
            return (<>{row.sharedWith.length} people</>);
        }
        else {        
            return (
                <div style={{display: 'flex', flexDirection: 'row', gap: 5}}>
                <>Not enough videos watched</>
                <Tooltip title={'This User\'s results will be available after the user has started watching videos'}>
                    <InfoOutlinedIcon sx={{color:'grey.600', width:20, height:20, cursor:'pointer'}}/>
                </Tooltip>
                </div>
            )   
        }
    }

    const getDialogTitle = (): string => {
        if(dialogToOpen === 'LOADING') {
            return 'Loading...';
        }
        else if(dialogToOpen === 'ADD') {
            if(selectedUsers.length > 0) {
                return selectedUsers.length > 1 
                    ? 'Multiple Users' 
                    : `Share ${selectedUsers[0].user.firstName} ${selectedUsers[0].user.lastName}'s Results`;
            }
            else {
                return 'Share Results';
            }
        }
        else {
            return 'View Insights Report';
        }
    }

    const getDialogWidth = (): string => {
        if (isMobile) {
            return '95%';
        } else if (isTablet) {
            switch(dialogToOpen) {
                case 'LOADING':
                    return '40%';
                case 'ADD':
                    return '85%';
                default:
                    return '60%';
            }
        } else {
            switch(dialogToOpen) {
                case 'LOADING':
                    return '25%';
                case 'ADD':
                    return '70%';
                default:
                    return '50%';
            }
        }
    }

    const getDialogHeight = (): string => {
        if (isMobile) {
            switch(dialogToOpen) {
                case 'LOADING':
                    return 'auto';
                case 'ADD':
                    return '90vh';
                default:
                    return 'auto';
            }
        } else {
            switch(dialogToOpen) {
                case 'LOADING':
                    return '25%';
                case 'ADD':
                    return '50%';
                default:
                    return '50%';
            }
        }
    }

    return (
        <>
        <Dialog open={dialogOpen} PaperProps={{sx: {width: getDialogWidth(), height: getDialogHeight()}}}>
          <DialogTitle>
            {getDialogTitle()}
            <IconButton aria-label="close" onClick={() => handleDialogClose(null)} sx={{position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500]}}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
            {getDialogToOpen()}
        </Dialog>
            <Container
                maxWidth={isMobile ? false : "lg"}
                disableGutters={isMobile}
                sx={{
                    py: { xs: 2, sm: 3, md: 4 },
                    px: { xs: 2, sm: 3, md: 3 },
                    width: '100%'
                }}>
                <Typography sx={{
                    fontWeight: 900,
                    fontSize: { xs: 20, sm: 24, md: 28 },
                    mb: { xs: 2, sm: 3 }
                }}>
                    Share Results
                </Typography>
                <Box sx={{
                    display:'flex',
                    flexDirection:'column',
                    gap: { xs: 2, sm: 3 }
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        mb: 1
                    }}>
                        <Button
                            variant='contained'
                            color='primary'
                            disabled={!(selectedUsers.length > 0)}
                            sx={{
                                height: { xs: 36, sm: 40 },
                                borderRadius: 2,
                                width: { xs: '100%', sm: 'auto' }
                            }}
                            startIcon={<ShortcutOutlinedIcon/>}
                            onClick={() => handleAddDialogOpen()}
                            aria-label="Share this report"
                        >
                            Share Results
                        </Button>
                    </Box>
                    <TableContainer component={Paper} sx={{
                        borderRadius: 2,
                        overflow: 'auto'
                    }}>
                        <Table sx={{ minWidth: { xs: 300, sm: 400 } }} size={isMobile ? 'small' : 'medium'} aria-label='Users & Invites'>
                            <TableHead sx={{'& th': {
                                color: 'grey.500',
                                fontSize: { xs: 12, sm: 14 }
                            }}}>
                                <TableRow>
                                    <TableCell sx={{ width: 25, padding: { xs: 1, sm: 2 } }}></TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Name</TableCell>
                                    <TableCell>{ isMobile ? 'User' : 'Email' }</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Shared to</TableCell>
                                    <TableCell align='center'>Preview</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {shareData?.map((row) => (
                                <TableRow key={row.user.id} sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                    <TableCell align='center' sx={{ width: 25, padding: { xs: 1, sm: 2 } }}>
                                        <Checkbox
                                            disabled={row.user.watchedCount && row.user.watchedCount > 0 ? false : true}
                                            onChange={(event) => handleSelectedUserChanged(row, event.target.checked)}
                                            size={isMobile ? 'small' : 'medium'}
                                        />
                                    </TableCell>
                                    <TableCell sx={{
                                        display: { xs: 'none', sm: 'table-cell' },
                                        color: row.user.watchedCount && row.user.watchedCount > 0 ? 'inherit' : 'grey.500',
                                        fontSize: { xs: 12, sm: 14 }
                                    }}>
                                        {row.user.firstName} {row.user.lastName}
                                    </TableCell>
                                    <TableCell sx={{
                                        color: row.user.watchedCount && row.user.watchedCount > 0 ? 'inherit' : 'grey.500',
                                        fontSize: { xs: 12, sm: 14 }
                                    }}>
                                        {isMobile ? `${row.user.firstName} ${row.user.lastName}` : row.user.email}
                                        {isMobile && (
                                            <Typography variant="caption" display="block" sx={{ color: 'grey.500' }}>
                                                {row.user.email}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{
                                        display: { xs: 'none', md: 'table-cell' },
                                        color: row.user.watchedCount && row.user.watchedCount > 0 ? 'inherit' : 'grey.500',
                                        fontSize: { xs: 12, sm: 14 }
                                    }}>
                                        {buildSharedWithOrTooltip(row)}
                                    </TableCell>
                                    <TableCell align='center'>
                                        <VisibilityOutlinedIcon sx={{
                                            color: row.user.watchedCount && row.user.watchedCount > 0 ? 'grey.600' : 'grey.500',
                                            width: { xs: 24, sm: 30 },
                                            height: { xs: 24, sm: 30 },
                                            cursor: row.user.watchedCount && row.user.watchedCount > 0 ? 'pointer' : 'default'
                                        }} onClick={async () => await handleViewPreviewDialogOpen(row.user)}/>
                                    </TableCell>
                                </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Container>
        </>
    );
}

export default ShareReportPage; 